/* Componente de lista de forum para moderação, responsável por exibir os tópicos do fórum e mensagens associadas que precisam ser moderadas. 

Esta listagem pode ser chamada do Painel do usuário de mais de um dos links de ferramentas do painel. Cada link passa um statusFiltro através do state. 

Status = 'enviado' - vai listar todas as mensagens que tiverem o status como enviado, juntamente com seu tópico principal caso seja uma mensagem de comentário de um tópico e não seja a principal. Vai habilitar os botões de Publicar ou Bloquear. 

Status = 'bloqueado' - vai listar todas as mensagens que tiverem o status como bloqueado, juntamente com seu tópico principal caso seja uma mensagem de comentário de um tópico e não seja a principal. Vai habilitar os botões de Publicar pois nesta listagem de mensagens bloqueadas serve para o admin ou editor avaliar mensagens bloqueadas caso seja necessário liberá-las. 

 * O componente usa o hook useEffect para buscar as mensagens assim que for montado, e o hook useState para armazenar a lista de mensagens no estado do componente.
 * O hook useNavigate é usado para redirecionar o usuário para a página de resultado em caso de erro.
 * A URL da requisição para buscar as mensagens é construída usando a variável de ambiente REACT_APP_API_URL, garantindo que a aplicação possa ser configurada para diferentes ambientes (desenvolvimento, produção, etc.) sem precisar alterar o código.
 *  Estilo básico usando classes do Bootstrap para a lista de mensagens.
 
*/
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import fotoIndisponivel from "../../assets/imagens/indisponivel.png";
import Botao from "../Botao";
import { useAuth } from "../../contextos/AuthContext"; // Importa o hook useAuth do contexto de autenticação para acessar o estado do usuário logado e funções relacionadas à autenticação
import Mensagem from "../Mensagem";

const ListaForumModeracao = () => {
    const location = useLocation(); // Hook para acessar o estado passado pela rota, que pode conter os dados do usuário logado ou outros dados relevantes para a exibição do fórum
    const navigate = useNavigate(); // Hook para redirecionar o usuário para outras páginas, como a página de resultado em caso de erro
    //Use o operador de coalescência nula (??). Ele só assume o valor padrão se o dado for estritamente null ou undefined, permitindo que a string vazia passe.
    const { token, dadosUsuario, logout } = useAuth(); // reage automaticamente ao login!
    const [carregando, setCarregando] = useState(true);// Estado para controlar o carregamento dos dados, inicialmente verdadeiro
    const [mensagem, setMensagem] = useState({ texto: '', tipo: '' });//Controla o 
    const [mensagens, setMensagens] = useState([]); // Estado para armazenar a lista de mensagens, inicialmente vazio

    // Valida se o token JWT ainda é válido antes de carregar a lista de moderação.
    // Se estiver expirado, a sessão é encerrada e o usuário é redirecionado para o login.
    const tokenExpirado = (jwt) => {
        if (!jwt) return true;

        try {
            const parts = jwt.split('.');
            if (parts.length < 2) return true;

            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            if (!payload.exp) return false;

            return Date.now() >= payload.exp * 1000;
        } catch {
            return true;
        }
    };
    
/* Esta função roda uma vez quando o componente é montado
/* Carrega a lista de mensagens do backend assim que o componente for exibido, garantindo que tenhamos os dados para mostrar na tela */    
useEffect(() => {

    const statusFiltro = location.state?.status ?? 'enviado'; // Define o status a ser filtrado. Neste componente traremos as mensagens status=enviado, as mensagens a serem moderadas

    // Se não houver token ou ele estiver expirado, encerra a sessão para evitar
    // carregar a lista com dados inconsistentes para um usuário já deslogado.
    if (!token || tokenExpirado(token)) {
        logout();
        navigate('/login', { state: { mensagem: 'Sessão expirada. Faça login novamente.' } });
        return null;
    }

    //Protege o acesso direto via url navegador
    if (!dadosUsuario || dadosUsuario.tipo === 'leitor') {
        navigate('/resultado', { 
            state: { 
            status: 'aviso',
            mensagem: 'Para acesso a esta página o usuário precisa estar logado como administrador.' 
                    }
            }); 
        return null;  
        }
    // Criamos um controlador de abort para permitir o cancelamento da requisição caso o componente seja desmontado antes da conclusão da requisição, evitando erros de memória e chamadas desnecessárias ao backend
    
    const controller = new AbortController();

    //Criamos uma função assíncrona para buscar as mensagens do backend, incluindo o token de autenticação no cabeçalho da requisição. Tem que ser criada dentro do useEffect porque o useEffect não pode ser assíncrono diretamente, mas pode chamar funções assíncronas. 
    const buscarMensagens = async () => {
        
        try{
            // Faz a requisição para o backend para buscar as mensagens, incluindo o token no cabeçalho de autorização
            
            //Usa a URL da sua variável de ambiente e completa a URL da requisição para buscar as mensagens, incluindo o status de filtro como parâmetro de consulta. O backend pode então usar esse parâmetro para filtrar as mensagens de acordo com o status desejado (enviado, liberado, bloqueado, etc.). 


            console.log(`Status recebido: ${statusFiltro}`);

            const url = `${process.env.REACT_APP_API_URL}/forum-list?status=${statusFiltro}`;

            const resposta = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    // 3. Envia o token via Header Authorization
                    'Authorization': `Bearer ${token}`
                },
                signal: controller.signal // ✅ Vincula o abort ao fetch
            });


            if (!resposta.ok) {
                    // Se o backend responder com 401/403, a sessão provavelmente expirou ou
                    // o usuário perdeu a autorização, então o fluxo força o logout e o login novamente.
                    if (resposta.status === 401 || resposta.status === 403) {
                        logout();
                        navigate('/login', { state: { mensagem: 'Sessão expirada. Faça login novamente.' } });
                        return;
                    }
                    throw new Error(`Erro na requisição: ${resposta.status}`);
                }
            
            const dadosRetorno = await resposta.json();
        
            if (dadosRetorno.status === 'sucesso') {
            // Em React, em vez de chamar uma função 'listarUsuarios', 
            // nós salvamos os dados no estado (useState)
            console.log(dadosRetorno.dados);    
            setMensagens(dadosRetorno.dados); // Salva as mensagens no estado para que os dados originalmente recebidos do backend estejam disponíveis para renderização e outras operações
            
            } else {
                navigate('/resultado', { 
                    state: { 
                    status: dadosRetorno.status,
                    mensagem: dadosRetorno.mensagem
                          }
                    });   
            }
        }catch(error){
            if (error.name === 'AbortError') return; // ✅ 1ª execução cancelada silenciosamente
            navigate('/resultado', { 
                    state: { 
                    status: "erro",
                    mensagem: "Ocorreu um erro ao buscar as mensagens: " + error.message
                          }
                    });
        }finally {
            setCarregando(false); // ✅ executa sempre — sucesso, erro ou vazio
        }
    }

    buscarMensagens(); // Chama a função para buscar as mensagens quando o componente for montado
    
    return () => controller.abort(); // ✅ Cancela quando desmonta (1ª execução do StrictMode)

}, [token, dadosUsuario, logout, navigate, location.state?.status]);

const updateStatusForum = async (comentario, novoStatus)=> {

    try {
        const url = `${process.env.REACT_APP_API_URL}/forum-update-status`;
        const resposta = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}` // Envia o token para autenticação
                
            },
            body: JSON.stringify({
                id: comentario.id,
                uuid: comentario.uuid,
                status: novoStatus
            })
        });

        if (resposta.ok) {
            const dadosRetorno = await resposta.json();
                if (dadosRetorno.status === 'sucesso') {
                    setMensagem({ texto: dadosRetorno.mensagem, tipo: dadosRetorno.status });
                    //Passamos o state para a página de resultado usando o navigate do react-router-dom, que permite enviar um objeto de estado junto com a navegação. Assim, a página de resultado pode acessar esse estado para exibir a mensagem apropriada. Só usaremos a página resultado para exibir mensagens de sucesso. Erros serão exibidos na própria página de cadastro para que o usuário possa corrigir sem perder os dados já preenchidos.
                            navigate('/resultado', { 
                                state: { 
                                    status: dadosRetorno.status,
                                    mensagem: dadosRetorno.mensagem
                                }
                                });
                            setCarregando (false); // Destrava o botão cadastrar após o processamento da requisição
                            return;
                } else {
                    // Se o erro for 401, significa que o token expirou durante a edição
                            if (resposta.status === 401 || resposta.status === 403) {
                                logout();
                                navigate('/login', { state: { mensagem: "Sessão expirada. Logue novamente." } });
                            return;
                            }
                            setMensagem({ texto: dadosRetorno.mensagem, tipo: dadosRetorno.status });
                            setCarregando (false); // Destrava o botão cadastrar após o processamento da requisição
                        return;
                }
        }else {
            /// Aqui o PHP enviou um erro o os dados possivelmente vieram vazios (ex: 500 ou 400 Bad Request)
            // ✅ Tenta parsear, mas trata se vier vazio
            let erroRetorno;
            try {
                erroRetorno = await resposta.json();
            } catch {
                erroRetorno = { mensagem: `Erro no servidor: ${resposta.status}`, status: 'erro' };
            }
            setMensagem({ 
                texto: erroRetorno.mensagem || "Erro ao processar.", 
                tipo: erroRetorno.status || 'erro'
            });
            setCarregando(false);
        }   

    } catch (error) {
        setMensagem({ texto: error.message, tipo: 'erro' });
        console.error("Erro ao fazer o update no status da mensagem:", error);
        setCarregando (false); // Destrava o botão cadastrar após o processamento da requisição
    }


}

// caso os dados do backend ainda estejam sendo carregados, exibe um spinner de carregamento para informar ao usuário que os dados estão sendo processados. Isso melhora a experiência do usuário, evitando que ele veja uma tela em branco ou incompleta enquanto os dados são carregados.
if (carregando) {
    return (
        <div className="container-fluid text-center mt-5">
            <div className="spinner-border text-success" role="status">
                <span className="visually-hidden"> Carregando... </span>
            </div>
        </div>
    );
}

// Renderiza a lista de mensagens do fórum, exibindo o título e a descrição de cada mensagem. Se não houver mensagens para exibir, mostra uma mensagem indicando que nenhum tópico foi encontrado. O campo de busca é exibido acima da lista de mensagens, permitindo ao usuário filtrar os tópicos com base no termo digitado.
if (mensagens.length === 0) {
        return (
            <div className="container-fluid justify-content-center">
                <h1 className="h1">Fórum de Estudos</h1>
                <p className="text-start fs-3">Nenhum tópico encontrado.</p>
            </div>
        );
    }

/* O código abaixo monta a estrutura de tópicos do fórum com as mensagens com status publicado*/
return (
        <div className="container-fluid justify-content-center">
            <h1 className="h1">Fórum de Estudos - Moderação de Mensagens</h1>
            <h2 className="h2 text-center bg-dark text-white-50 fw-semibold">{
                    (location.state?.status==="enviado" && ("Mensagens enviadas para moderação")) ||
                    (location.state?.status==="bloqueado" && ("Mensagens bloqueadas para publicação"))
                }
            </h2>
                <div className="container-fluid">
                    
                    <p className="fs-3">Esta página é destinada a avaliação das mensagens enviadas pelos usuários ao fórum. </p>
                    <p className="fs-3">Importante saber que quando temos comentários de um tópico a serem avaliados, listamos também o tópico principal para facilitar a interpretação da mensagem a ser publicada. </p>
                    <p className="fs-3">As mensagens estão sinalizadas com cores e marcadores específicos para o moderador saber quais são tópicos principais e respostas/mensagens relacionadas, bem como as mensagens/tópicos já publicados e os que estão aguardando liberação.  </p>
                    <p className="fs-3">Se a listagem for de mensagens bloqueadas, o usuário admin ou editor poderá publicá-la caso seja necessário ou por ter sido bloqueada por engano. </p>
                    <p>Somente o usuário do tipo admin terá acesso aos botões de publicar ou bloquear indepentedete de ser proprietário da mensagem ou não. Usuários proprietários da mensagem não poderão publicar ou bloquear suas próprias mensagens.</p>
                </div>
                <div className="container-fluid mb-3 bg-dark-subtle p-3 rounded">
                    <Mensagem mensagem={mensagem.texto} tipo={mensagem.tipo} /> 
                    {/* Renderiza a lista de mensagens filtradas, exibindo o título e a descrição de cada mensagem. Se não houver mensagens filtradas para exibir, mostra uma mensagem indicando que nenhum tópico foi encontrado. A renderização da lista de mensagens é feita com base na lista de mensagens filtradas, que é atualizada conforme o usuário digita no campo de busca. */}
                    {mensagens.length === 0 && (
                        <div className="container-fluid justify-content-center">
                            <p className="text-start fs-3">Nenhum tópico encontrado.</p>
                        </div>
                    )}

                    {mensagens.map((comentario) => {
                        // Extrai o nome antes do @ do email
                        const nomeUsuario = (comentario.usuario || '').split('@')[0]; // Extrai o nome antes do @ do email
                        // Define a foto, usando a imagem padrão se não houver foto cadastrada
                        const urlFoto = comentario.foto 
                        ? `${process.env.REACT_APP_API_URL}/public/assets/imagens/usuarios/${comentario.foto}` 
                        : fotoIndisponivel; // Usa a imagem padrão se não houver foto cadastrada

                        return (
                            /* Para montar os cards com as mensagen vamos montar diferenciando os tópicos principais das mensagen de respostas. BackGround terão cores diferentes dependendo se forem tópicos ou respostas. 
                            */
                            <div key={comentario.uuid} className={`container-fluid d-flex mb-3 p-3 rounded
                                    ${comentario.idResposta === null 
                                            ? 'bg-warning-subtle'   // ← mensagem principal
                                            : 'bg-light ms-3 me-2 w-auto'            // ← resposta
                                    }`}
                            >
                            
                                <div className={`d-flex flex-column align-items-center justify-content-center mb-2 gap-2`}
                                >
                                    <img 
                                    src={urlFoto} 
                                    alt={`Foto de ${nomeUsuario}`}
                                    style={{ width: '45px', height: '45px', objectFit: 'cover' }}
                                    className="rounded-circle border"
                                    />
                                    <span className="fw-bold fs-6">@{nomeUsuario}</span>
                                </div>

                                <div className="d-flex flex-column ms-3">
                                    {/* Sinalização baseada no idResposta */}
                                    {comentario.idResposta === null ? (
                                        <span className="mb-1 badge bg-success p-2 text-white bg-opacity-75">
                                            <i className="bi bi-pin-angle me-1"></i>
                                            Tópico principal
                                        </span>
                                    ) : (
                                        <span className="mb-1 badge bg-success p-2 text-white  bg-opacity-50">
                                            <i className="bi bi-arrow-return-right me-1"></i>
                                            Resposta
                                        </span>
                                    )}
                                    

                                        {comentario.status === 'publicado' ?(
                                            <span className="mb-1 badge text-bg-primary">
                                                {comentario.status}
                                            </span>    
                                        ):
                                        (
                                            <>
                                                <span className="mb-1 badge text-bg-warning">
                                                    {comentario.status}
                                                </span>
                                                {/*A arrow function () => é necessária quando você precisa passar argumentos para a função. Sem ela o React executaria a função imediatamente ao renderizar o componente, e não quando o usuário clicasse.*/}
 
                                                {/*Se o usuário for admin ou o usuário da mensagem for diferente do usuário logado libera o botão para publicação*/}
                                                {(dadosUsuario?.tipo=== "admin" || dadosUsuario?.user !== comentario.usuario) && (
                                                <Botao className="btn btn-success mb-3"
                                                    aoClicar={()=>updateStatusForum(comentario, 'publicado')}
                                                >
                                                    Publicar        
                                                </Botao>
                                                 )}
                                                
                                                {/*Se o usuário for admin ou o usuário da mensagem for diferente do usuário logado libera o botão para bloquear, desde que  o status for "enviado"*/}
                                                {(dadosUsuario?.tipo === "admin" || dadosUsuario?.user !== comentario.usuario) && (comentario.status === "enviado") && (
                                                    <Botao className="btn btn-danger mb-3"
                                                        aoClicar={()=>updateStatusForum(comentario, 'bloqueado')}
                                                    >
                                                        Bloquear        
                                                    </Botao>
                                                )}

                                            </>    
                                        )}
                                </div>    
                                
                                <div className="d-flex flex-column ms-3">
                                    <h2 className="h2">{comentario.assunto}</h2>
                                    <p className="text-start fs-5">{comentario.comentario}</p>
                                </div>
                                

                            </div>
                        );
                    })}
                </div>
        </div>    
)

}

export default ListaForumModeracao;
